import React from 'react';
import { Form, FormProps } from '@doveit/bricks';
import { NonLegalBuyerAmlValidationSchema } from './schema';
import { BooleanString } from '../../../../../utils/boolean-string/booleanString';

export interface NonLegalBuyerAmlFormProps {
  initialValues?: Partial<NonLegalBuyerAmlFormModel>,
  loading?: FormProps<NonLegalBuyerAmlFormModel>['loading'],
  disabled?: FormProps<NonLegalBuyerAmlFormModel>['disabled'],
  formRef?: FormProps<NonLegalBuyerAmlFormModel>['innerRef'],
  onSubmit: FormProps<NonLegalBuyerAmlFormModel>['onSubmit']
}

export interface NonLegalBuyerAmlFormModel {
  politicallyExposed: BooleanString,
  relatedToAPoliticallyExposedFigure: BooleanString,
  mortgageMoneySource: boolean,
  personalMoneySource: boolean,
  sellingPersonalPropertiesMoneySource: boolean,
  otherMoneySource: boolean,
  otherMoneySourceValue?: string,
  notes?: string,
}

const DEFAULT_INITIAL_VALUES: NonLegalBuyerAmlFormModel = {
  politicallyExposed: '',
  relatedToAPoliticallyExposedFigure: '',
  mortgageMoneySource: false,
  personalMoneySource: false,
  sellingPersonalPropertiesMoneySource: false,
  otherMoneySource: false,
  otherMoneySourceValue: '',
  notes: '',
};

const NonLegalBuyerAmlForm: React.FC<NonLegalBuyerAmlFormProps> = ({
  initialValues,
  disabled,
  formRef,
  onSubmit,
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...DEFAULT_INITIAL_VALUES,
    ...initialValues,
  }), [initialValues]);

  return (
    <Form
      initialValues={mergedInitialValues}
      onSubmit={onSubmit}
      innerRef={formRef}
      disabled={disabled}
      validationSchema={NonLegalBuyerAmlValidationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue }) => {
        const onOtherMoneySourceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (!e.target.checked) {
            setFieldValue('otherMoneySourceValue', '');
          }
        };

        return (
          <>
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.YesOrNotButtons
                aria-label="Campo per indicare se la persona è politicamente esposta"
                label="Persona politicamente esposta"
                name="politicallyExposed"
              />
            </Form.Item>
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.YesOrNotButtons
                aria-label="Campo per indicare se parente o convivente di persona politicamente esposta"
                label="Parente o convivente di persona politicamente esposta"
                name="relatedToAPoliticallyExposedFigure"
              />
            </Form.Item>

            <Form.Fieldset legend="Provenienza del denaro">
              <Form.Item>
                <Form.Checkbox
                  aria-label="Campo per indicare se il denaro è proveniente da mutuo"
                  name="mortgageMoneySource"
                  text="Mutuo"
                />
              </Form.Item>
              <Form.Item>
                <Form.Checkbox
                  aria-label="Campo per indicare se il denaro è proveniente da redditi o risparmi propri"
                  name="personalMoneySource"
                  text="Redditi o risparmi propri"
                />
              </Form.Item>
              <Form.Item>
                <Form.Checkbox
                  aria-label="Campo per indicare se il denaro è proveniente dalla vendita di beni propri"
                  name="sellingPersonalPropertiesMoneySource"
                  text="Ricavato dalla vendita di beni propri"
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Checkbox
                  aria-label="Campo per indicare se il denaro è proveniente da altre sorgenti"
                  name="otherMoneySource"
                  text="Altro"
                  onChange={onOtherMoneySourceChange}
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  aria-label="Campo per indicare altre sorgenti di denaro"
                  name="otherMoneySourceValue"
                  disabled={!values.otherMoneySource}
                  label="Specificare"
                />
              </Form.Item>
            </Form.Fieldset>
            <Form.Item>
              <Form.TextArea
                name="notes"
                label="Osservazioni e note"
                rows={5}
              />
            </Form.Item>
          </>
        );
      }}
    </Form>
  );
};

export default NonLegalBuyerAmlForm;
